/**
 * SearchBar Component
 * Global search bar with autocomplete suggestions
 * Accessible via keyboard shortcut (Cmd/Ctrl + K)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  InputBase,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Storage as Database,
  Shield,
  Description,
  Person,
  History,
  TrendingUp,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  getSearchSuggestions,
  getRecentSearches,
  SearchSuggestion,
  RecentSearch
} from '../../services/searchService';

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#003366',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: theme.spacing(1),
    transition: theme.transitions.create('width'),
    width: '100%',
    '&::placeholder': {
      color: '#555',
      opacity: 0.7,
    },
  },
}));

const SuggestionsDropdown = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: theme.spacing(0.5),
  maxHeight: '400px',
  overflowY: 'auto',
  zIndex: 1300,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  border: '1px solid #e0e0e0',
}));

interface SearchBarProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder = 'Search our site'
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Get icon for content type
  const getIcon = (iconName: string) => {
    switch (iconName.toLowerCase()) {
      case 'database':
        return <Database fontSize="small" sx={{ color: '#003366' }} />;
      case 'shield':
        return <Shield fontSize="small" sx={{ color: '#003366' }} />;
      case 'description':
        return <Description fontSize="small" sx={{ color: '#003366' }} />;
      case 'person':
        return <Person fontSize="small" sx={{ color: '#003366' }} />;
      default:
        return <SearchIcon fontSize="small" sx={{ color: '#003366' }} />;
    }
  };

  // Load recent searches when dropdown opens
  const loadRecentSearches = useCallback(async () => {
    try {
      const recent = await getRecentSearches(5);
      setRecentSearches(recent);
    } catch (error) {
      console.error('Failed to load recent searches:', error);
    }
  }, []);

  // Fetch suggestions with debouncing
  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await getSearchSuggestions(searchQuery, 5);
      setSuggestions(results);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debouncing
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  // Handle form submission
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (query.trim()) {
      performSearch(query);
    }
  };

  // Perform search and navigate to results page
  const performSearch = (searchQuery: string) => {
    setShowSuggestions(false);
    setQuery('');
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestionText: string) => {
    performSearch(suggestionText);
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const totalItems = suggestions.length + recentSearches.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev < totalItems - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          event.preventDefault();
          const allItems = [...suggestions, ...recentSearches];
          const selectedItem = allItems[selectedIndex];
          if (selectedItem) {
            const searchText = 'text' in selectedItem ? selectedItem.text : selectedItem.query;
            performSearch(searchText);
          }
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    setShowSuggestions(true);
    if (query.length === 0) {
      loadRecentSearches();
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Clear query
  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const showRecentSearches = query.length === 0 && recentSearches.length > 0;
  const showSuggestionsDropdown = showSuggestions && (suggestions.length > 0 || showRecentSearches || isLoading);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          border: '1px solid #bbb',
          boxShadow: 'none',
          borderRadius: 1,
          '&:focus-within': {
            borderColor: '#003366',
            boxShadow: '0 0 0 2px rgba(0, 51, 102, 0.1)',
          },
        }}
      >
        <StyledInputBase
          inputRef={inputRef}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          inputProps={{
            'aria-label': 'search the site',
            'aria-autocomplete': 'list',
            'aria-controls': showSuggestionsDropdown ? 'search-suggestions' : undefined,
            'aria-expanded': showSuggestionsDropdown,
            'accessKey': 's',
            'role': 'searchbox',
          }}
          fullWidth
        />
        {query && (
          <IconButton
            onClick={handleClear}
            aria-label="clear search"
            size="small"
            sx={{ color: '#666' }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}
        <IconButton
          type="submit"
          aria-label="search"
          sx={{ color: '#003366' }}
          disabled={!query.trim()}
        >
          <SearchIcon />
        </IconButton>
      </Paper>

      {/* Suggestions Dropdown */}
      {showSuggestionsDropdown && (
        <SuggestionsDropdown id="search-suggestions" role="listbox">
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} sx={{ color: '#003366' }} />
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {/* Recent Searches */}
              {showRecentSearches && (
                <>
                  <ListItem sx={{ py: 1, px: 2, backgroundColor: '#f5f5f5' }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <History fontSize="small" sx={{ color: '#666' }} />
                    </ListItemIcon>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                      Recent Searches
                    </Typography>
                  </ListItem>
                  {recentSearches.map((recent, index) => (
                    <ListItemButton
                      key={recent._id}
                      selected={selectedIndex === index}
                      onClick={() => handleSuggestionClick(recent.query)}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 51, 102, 0.08)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 51, 102, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <History fontSize="small" sx={{ color: '#999' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={recent.query}
                        secondary={`${recent.resultsCount} results`}
                      />
                    </ListItemButton>
                  ))}
                  {suggestions.length > 0 && <Divider />}
                </>
              )}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <>
                  <ListItem sx={{ py: 1, px: 2, backgroundColor: '#f5f5f5' }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <TrendingUp fontSize="small" sx={{ color: '#666' }} />
                    </ListItemIcon>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                      Suggestions
                    </Typography>
                  </ListItem>
                  {suggestions.map((suggestion, index) => {
                    const adjustedIndex = showRecentSearches ? index + recentSearches.length : index;
                    return (
                      <ListItemButton
                        key={`${suggestion.text}-${index}`}
                        selected={selectedIndex === adjustedIndex}
                        onClick={() => handleSuggestionClick(suggestion.text)}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 51, 102, 0.08)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(0, 51, 102, 0.04)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getIcon(suggestion.icon)}
                        </ListItemIcon>
                        <ListItemText
                          primary={suggestion.text}
                          secondary={suggestion.type}
                        />
                      </ListItemButton>
                    );
                  })}
                </>
              )}

              {/* No results */}
              {!isLoading && query.length >= 2 && suggestions.length === 0 && !showRecentSearches && (
                <ListItem>
                  <ListItemText
                    primary="No suggestions found"
                    secondary="Press Enter to search"
                    sx={{ textAlign: 'center', color: '#666' }}
                  />
                </ListItem>
              )}
            </List>
          )}
        </SuggestionsDropdown>
      )}

      {/* Keyboard shortcut hint */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 48,
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#999',
          pointerEvents: 'none',
          display: { xs: 'none', md: 'block' },
        }}
      >
        ⌘K
      </Typography>
    </Box>
  );
};

export default SearchBar;
